import { useEffect } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { Card } from '@siliconx/shared/ui/Card';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import {
  REGULATORY_REGISTER_ANCHOR_ID,
  REGULATORY_REGISTER_HEADING_WITH_COUNT,
  REGULATORY_REGISTER_INTRO,
  REGULATORY_REGISTER_ROWS,
} from './regulationCopy';

/**
 * SIL-611 — Reports tab compliance register (design option 1d). A NEW
 * component, deliberately NOT a reuse of `DisclosureMappingCard` — that
 * card takes no props, hardcodes a single download path, and its trailing
 * legal paragraph is SIL-459-register locked and false for a regulation
 * dataset. Only the visual shell (card chrome/typography) is copied, so the
 * Reports tab reads as one language. Columns: Regulation / Jurisdiction /
 * Applies to / Reference. Four rows, all four shown to every org, always —
 * the Jurisdiction column carries the CA-specific scoping, never
 * conditional display (SIL-611 decision 3).
 */
export function RegulatoryRegisterCard() {
  const hash = useRouterState({ select: (s) => s.location.hash });

  // SIL-611: the cross-link on the other environmental surfaces points at
  // `/environmental/reports#regulatory-register`. The router handles that hash
  // at navigation time — but this card sits below the Reports data, so it has
  // not mounted yet and there is nothing to scroll to. The URL updated and the
  // page stayed at the top. Scrolling here, once the card is actually in the
  // DOM, is what makes the link land on the register.
  useEffect(() => {
    if (hash.replace(/^#/, '') !== REGULATORY_REGISTER_ANCHOR_ID) return;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.getElementById(REGULATORY_REGISTER_ANCHOR_ID)?.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }, [hash]);

  return (
    <Card padding="md" className="flex flex-col gap-3" id={REGULATORY_REGISTER_ANCHOR_ID}>
      <SectionLabel>{REGULATORY_REGISTER_HEADING_WITH_COUNT}</SectionLabel>
      <p className="text-xs leading-relaxed text-zinc-500">{REGULATORY_REGISTER_INTRO}</p>
      <div className="flex flex-col divide-y divide-zinc-100 rounded-md border border-zinc-200">
        {REGULATORY_REGISTER_ROWS.map((row) => (
          <div
            key={row.key}
            data-testid={`regulatory-register-row-${row.key}`}
            className={`flex flex-col gap-2 p-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 ${
              row.badge ? 'bg-amber-50/70' : ''
            }`}
          >
            <div className="flex flex-col gap-0.5 sm:w-1/4 sm:shrink-0">
              <span className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-zinc-800">
                {row.regulation}
                {row.badge && (
                  <span className="rounded bg-amber-200 px-1 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-900">
                    {row.badge}
                  </span>
                )}
              </span>
              <span className="text-xs text-zinc-500">{row.jurisdiction}</span>
            </div>
            <p className="text-xs leading-relaxed text-zinc-500 sm:flex-1">{row.appliesTo}</p>
            <div className="sm:w-24 sm:shrink-0 sm:text-right">
              <a
                href={row.referenceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-medium text-brand-700 hover:underline"
              >
                {row.referenceLabel}
              </a>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
